import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {scrollIntoView} from '~/utils/scrolling';
import renderInlineSvg from '~/utils/renderInlineSvg';
import {rightArrowIcon} from '~/constants/InlineSvgs';

import './index.scss';

const STEP_DIRECTION = {
  forward: 1,
  back: -1
};

const propTypes = {
  className: PropTypes.string,
  options: PropTypes.array,
  renderOption: PropTypes.func,
  selectedIndex: PropTypes.number,
  showArrows: PropTypes.oneOf(['always', 'overflow', 'never']),
  onChange: PropTypes.func
};

/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
export default class SelectorList extends Component {
  static propTypes = propTypes;

  static defaultProps = {
    options: [],
    showArrows: 'overflow',
    renderOption(o) {
      return o;
    }
  };

  componentDidMount() {
    this.ensureItemInView(this.props.selectedIndex);
  }

  componentDidUpdate(prevProps) {
    this.ensureItemInView(
      this.props.selectedIndex,
      prevProps.selectedIndex !== this.props.selectedIndex
    );
  }

  ensureItemInView(itemIndex, shouldScroll = true) {
    const overflowing = this.updateOverflowStatus();
    if (overflowing && shouldScroll) {
      scrollIntoView(this.getItemNode(itemIndex), this._selectors, 'x');
    }
  }

  getItemNode(index) {
    return this._selectors.childNodes[index];
  }

  updateOverflowStatus() {
    if (!this?._node || !this?._selectors) return false;

    const overflowing =
      this._selectors &&
      this._selectors.scrollWidth > this._selectors.clientWidth;
    if (overflowing) {
      this._node.classList.add('is-overflowing');
    } else {
      this._node.classList.remove('is-overflowing');
    }
    return overflowing;
  }

  showArrows() {
    return (
      this.props.showArrows === 'always' || this.props.showArrows === 'overflow'
    );
  }

  handlePreviewChange(index) {
    if (this.props.selectedIndex !== index) {
      this.props.onChange &&
        this.props.onChange(this.props.options[index], index);
    }
  }

  updateSelection(dir) {
    const newIndex =
      (this.props.selectedIndex + dir + this.props.options.length) %
      this.props.options.length;
    this.handlePreviewChange(newIndex);
  }

  renderSelectorOption = (option, index) => {
    const selected = this.props.selectedIndex === index;
    const classes = classnames('ndx-SelectorList-option', {
      'is-selected': selected
    });
    const clickHandler = () => this.handlePreviewChange(index);
    let optionElement = this.props.renderOption(option, index);
    if (optionElement && !React.isValidElement(optionElement)) {
      optionElement = <div>{`${optionElement}`}</div>;
    }
    return (
      <div key={index} className={classes} onClick={clickHandler}>
        {optionElement && React.cloneElement(optionElement, {selected})}
      </div>
    );
  };

  renderControl(step) {
    const classes = classnames(
      'ndx-SelectorList-control',
      `ndx-SelectorList-control--${step}`,
      {'is-disabled': !this.props.onChange}
    );
    return (
      this.showArrows() && (
        <span
          className={classes}
          dangerouslySetInnerHTML={renderInlineSvg(rightArrowIcon)}
          onClick={() => this.updateSelection(STEP_DIRECTION[step])}
        />
      )
    );
  }

  render() {
    const {options, className} = this.props;
    return (
      !!options.length && (
        <div
          ref={n => (this._node = n)}
          className={classnames('ndx-SelectorList', className)}
          data-arrows={this.props.showArrows}>
          {this.renderControl('back')}
          <div
            ref={node => (this._selectors = node)}
            className="ndx-SelectorList-optionContainer">
            {options.map(this.renderSelectorOption)}
          </div>
          {this.renderControl('forward')}
        </div>
      )
    );
  }
}
